import axios from "axios";

const API_ROOT = import.meta.env.VITE_APP_BASE_URL;

if (!API_ROOT) {
  console.error(
    "VITE_APP_BASE_URL is not set. Add it to .env (dev), .env.production " +
      "(build/preview), and Cloudflare Pages → Settings → Environment " +
      "variables (live deploys), then rebuild."
  );
}

export const BASE_URL = `${API_ROOT || ""}/api/v1/partner`;

const TOKEN_KEY = "alfennzo.accessToken";
const REFRESH_KEY = "alfennzo.refreshToken";
const PARTNER_KEY = "alfennzo.partner";

export const tokenStore = {
  getAccessToken: () => sessionStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => sessionStorage.getItem(REFRESH_KEY),
  getPartner: () => {
    const raw = sessionStorage.getItem(PARTNER_KEY);
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      // Sessions written by an older build stored the whole response envelope
      // rather than the Partner object. Unwrap those so `partner.kind` etc.
      // resolve on the very first render instead of after /auth/me returns.
      if (parsed && typeof parsed === "object" && "success" in parsed && parsed.data) {
        return parsed.data;
      }
      return parsed;
    } catch {
      return null;
    }
  },
  setSession: ({ accessToken, refreshToken, partner }) => {
    if (accessToken) sessionStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) sessionStorage.setItem(REFRESH_KEY, refreshToken);
    if (partner) sessionStorage.setItem(PARTNER_KEY, JSON.stringify(partner));

    // ✅ CRITICAL: Set the auth header immediately on the api instance
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  },
  clear: () => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(PARTNER_KEY);
    // ✅ CRITICAL: Clear the auth header
    delete api.defaults.headers.common['Authorization'];
  },
  // Helper to check if token is expired
  isTokenExpired: () => {
    const token = tokenStore.getAccessToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  timeout: 30000,
});

// ✅ Request interceptor - ensure token is always added
api.interceptors.request.use(
  (config) => {
    if (config.url?.includes("/auth/") && !config.url?.includes("/auth/me")) {
      return config;
    }
    const token = tokenStore.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ✅ Response interceptor - Handle 401 and token refresh
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

let onAuthFailure = () => {};

export function registerAuthFailureHandler(handler) {
  onAuthFailure = handler;
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isAuthRoute = originalRequest?.url?.includes("/auth/");
    if (status === 401 && !originalRequest._retry && !isAuthRoute) {
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = tokenStore.getRefreshToken();
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Uses BASE_URL (now absolute) instead of a bare relative path —
        // this call previously broke in preview/production for the same
        // reason as every other request.
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = data.data;
        tokenStore.setSession({
          accessToken,
          refreshToken: newRefreshToken || refreshToken
        });

        isRefreshing = false;
        onRefreshed(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);

      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];

        tokenStore.clear();
        if (onAuthFailure) {
          onAuthFailure();
        }
        return Promise.reject(refreshError);
      }
    }

    if (!error.response) {
      return Promise.reject(new Error('Network error - please check your connection'));
    }

    return Promise.reject(error);
  }
);

export const setAuthHeader = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export default api;